/**
 * Globale Schnellsuche (Command-Palette) fuer die Topbar.
 * - Klick aufs Header-Feld ODER ⌘K / Strg+K oeffnet die Palette.
 * - Debounced GET /api/search?q= (tenant-sicher, ILIKE-Teilwortsuche ueber Anfragen,
 *   Kunden, Buchungen, Rechnungen, Katalog), Treffer gruppiert, Tastatur-Navigation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useDebounced } from "../hooks/useDebounced";
import { useOverlayOpenSignal } from "../hooks/useOverlayOpen";
import { abrechnungPfad, vorgangPfad } from "../lib/pfade";
import { Icon, StatusPill, istBrowserEigenerKlick } from "./components";
import { eventStatusMeta } from "./format";

interface SearchHit {
  type: "customer" | "event" | "invoice" | "catalog";
  id: string;
  title: string;
  subtitle: string | null;
  meta: string | null;
}
interface SearchResult {
  query: string;
  hits: SearchHit[];
}

const TYPE_META: Record<SearchHit["type"], { label: string; icon: string; href: (id: string) => string }> = {
  // Warteschlangen-Punkt 14.1 (14.09.2026, Antoine "ja"): vormals ein eigener `inquiry`-Trefftyp
  // ("Anfragen") — führte aber seit AP5 auf DIESELBE Route wie `event` (`vorgangPfad(id)`, beide
  // sind seit "eine Identität" derselbe Vorgang). Traf ein Suchbegriff beide gleichzeitig, sah
  // der Betreiber denselben Vorgang doppelt. `routes/search.ts` fasst die frühere Anfrage-Abfrage
  // jetzt in die Veranstaltungs-Abfrage zusammen — ein Trefftyp, eine Zeile pro Vorgang.
  event: { label: "Buchungen", icon: "event", href: (id) => vorgangPfad(id) },
  // Fund 16.10.2026: Rechnungen/Gutschriften per Nummer; `id` ist die Vorgangs-ID (routes/search.ts),
  // das Ziel die Abrechnung des Vorgangs, deren Übersicht die Belege listet.
  invoice: { label: "Rechnungen", icon: "receipt_long", href: (id) => abrechnungPfad(id) },
  customer: { label: "Kunden", icon: "group", href: (id) => `/kunden/${id}` },
  // Katalog-Positionen haben (Stand 28.08.2026) KEINE eigene Adresse - sie werden ausschliesslich
  // ueber einen Bearbeiten-Dialog INNERHALB von /katalog geoeffnet (`openEdit()` in
  // `pages/catalog.tsx`, kein `/katalog/:id`-Route, kein deep-linkbarer Query-Parameter). Anders
  // als bei `event` gibt es hier also kein Ziel, das die id ignoriert haette treffen koennen -
  // `/katalog` bleibt bewusst die korrekte, einzig moegliche Zieladresse fuer diesen Treffertyp.
  catalog: { label: "Katalog", icon: "menu_book", href: () => "/katalog" },
};
const GROUP_ORDER: SearchHit["type"][] = ["event", "invoice", "customer", "catalog"];

/**
 * Fund 08.09.2026 (Antoine, Screenshot): `hit.meta` trug bei Veranstaltungen den rohen DB-Status
 * ("quoted", "offer_chosen", …) direkt in die Trefferliste - verstaendlich fuer den Code, nicht
 * fuer den Betrieb ("Ich weiss nicht was was ist"). Dieselbe Label-/Farb-Tabelle wie die
 * Listenansicht (`eventStatusMeta`) sorgt dafuer, dass ein Status ueberall im Produkt gleich
 * heisst - Kunden/Katalog-Treffer tragen keinen Status, ihr `meta` (Stadt/Preis) bleibt der
 * bisherige schlichte Text-Badge.
 */
function renderMeta(hit: SearchHit) {
  if (!hit.meta) return null;
  if (hit.type === "event") return <StatusPill meta={eventStatusMeta(hit.meta)} />;
  return (
    <span className="shrink-0 rounded-md bg-surface-container-high px-1.5 py-0.5 font-label-md text-[10.5px] text-on-surface-variant dark:bg-white/5">
      {hit.meta}
    </span>
  );
}


export function GlobalSearch({ open, onClose, startBegriff = "" }: { open: boolean; onClose: () => void; startBegriff?: string }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term.trim(), 180);
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(0);
  useOverlayOpenSignal(open);

  const path = open && debounced.length >= 2 ? `/api/search?q=${encodeURIComponent(debounced)}` : null;
  const { data, loading } = useApi<SearchResult>(path);
  const hits = useMemo(() => data?.hits ?? [], [data]);

  // Beim Oeffnen: Feld auf den Startbegriff (meist leer) setzen + fokussieren.
  useEffect(() => {
    if (open) {
      setTerm(startBegriff);
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open, startBegriff]);
  useEffect(() => setActive(0), [debounced]);

  const go = useCallback(
    (hit: SearchHit) => {
      onClose();
      navigate(TYPE_META[hit.type].href(hit.id));
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && hits[active]) {
      e.preventDefault();
      go(hits[active]!);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  // Gruppieren fuer die Anzeige; flache Reihenfolge fuer Tastatur (active-Index).
  const grouped = GROUP_ORDER.map((type) => ({ type, items: hits.filter((h) => h.type === type) })).filter((g) => g.items.length > 0);
  let flatIndex = -1;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-[12vh]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[70vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl dark:bg-night-card" role="dialog" aria-label="Suche">
        <div className="flex items-center gap-2 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
          <Icon name="search" className="text-[20px] text-on-surface-variant" />
          <input
            ref={inputRef}
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Anfragen, Kunden, Buchungen, Rechnungen, Katalog durchsuchen …"
            className="flex-1 bg-transparent text-[15px] text-on-surface outline-none placeholder:text-on-surface-variant/70 dark:text-inverse-on-surface"
          />
          {loading && <Icon name="progress_activity" className="animate-spin text-[18px] text-on-surface-variant" />}
          <kbd className="hidden items-center rounded-md border border-outline-variant/60 bg-surface-container-lowest px-1.5 py-0.5 font-sans text-[10.5px] font-semibold text-on-surface-variant sm:inline-flex dark:border-white/10 dark:bg-white/5">
            Esc
          </kbd>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-2">
          {debounced.length < 2 ? (
            <p className="px-4 py-8 text-center font-body-sm text-body-sm text-on-surface-variant/70">
              Mindestens 2 Zeichen eingeben. Navigation mit ↑ ↓, Öffnen mit ⏎.
            </p>
          ) : !loading && hits.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <Icon name="search_off" className="text-3xl text-on-surface-variant/60" />
              <p className="font-body-sm text-body-sm text-on-surface-variant">Keine Treffer für „{debounced}“.</p>
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.type} className="mb-1">
                <div className="px-4 py-1 font-label-md text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant/70">
                  {TYPE_META[g.type].label}
                </div>
                {g.items.map((hit) => {
                  flatIndex++;
                  const idx = flatIndex;
                  return (
                    /* Einheit E (`docs/PLAN-VORGANGSSEITE-UMBAU.md`, 13.09.2026): war ein
                       `<button onClick={go}>` — das Ziel stand mit `TYPE_META[].href(id)` schon
                       laengst fest, der Browser bekam es nur nie zu sehen. Als echter `<Link>`
                       funktionieren Cmd-/Strg-/Mittelklick, Rechtsklick und Statuszeile.
                       `istBrowserEigenerKlick`: bei einem modifizierten Klick uebernimmt der
                       Browser (neuer Tab) — die Palette bleibt dann bewusst offen, statt die
                       Oberflaeche unter dem neuen Tab wegzuraeumen. */
                    <Link
                      key={`${hit.type}-${hit.id}-${hit.subtitle ?? ""}`}
                      to={TYPE_META[hit.type].href(hit.id)}
                      onMouseEnter={() => setActive(idx)}
                      onClick={(e) => {
                        if (!istBrowserEigenerKlick(e)) onClose();
                      }}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${
                        active === idx ? "bg-primary/10" : "hover:bg-surface-container-high dark:hover:bg-white/5"
                      }`}
                    >
                      <Icon name={TYPE_META[hit.type].icon} className="shrink-0 text-[18px] text-on-surface-variant" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-medium text-on-surface dark:text-inverse-on-surface">{hit.title}</span>
                        {hit.subtitle && <span className="block truncate font-body-sm text-[12px] text-on-surface-variant/80">{hit.subtitle}</span>}
                      </span>
                      {renderMeta(hit)}
                    </Link>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
